import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ChatModel, ChatMessagesModel } from 'models/ChatModel';
import auth from 'recoil/atoms/auth';
import Api from 'utils/api';
import { removeItemAtIndex } from 'utils/common';


export type ChatSender = (text: string, attachments?: File[]) => void;

const messageLimit = 20;
const appId = process.env.REACT_APP_API_UUID;

export const useChat = (code: string, name: string) => {
    const my = useRecoilValue(auth);
    const [loading, setLoading] = useState(false);
    const [chat, setChat] = useState<ChatMessagesModel>({
        messages: [],
        hasMore: false,
        pending: true,
    });

    useEffect(() => {
        Api.chats.createOrGet(code, name)
            .then(res => setChat({
                messages: res.data.data.messages,
                hasMore: res.data.data.messages.length >= messageLimit,
                pending: false,
            }));
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const callback = ({chat, message}: { message: any, chat: any }) => {
            if (code === chat.code && message.author.id !== my.id)
                setChat(old => ({
                    ...old,
                    messages: [...old.messages, message],
                }));
        }

        window.Echo.private(`${appId}.App.Models.User.${my.id}`)
            .listen('MessageSent', callback)

        return () => {
            window.Echo.private(`${appId}.App.Models.User.${my.id}`)
                .stopListening('MessageSent', callback)
        }
        // eslint-disable-next-line
    }, []);

    const send: ChatSender = (text, attachments) => {
        Api.chats.send(code, text, attachments)
            .then(res => setChat(old => ({
                ...old,
                messages: [...old.messages, res.data.data],
            })));
    };

    const loadMore = () => {
        setLoading(true);
        Api.chats
            .prev(code, chat.messages[0].createdAt)
            .then(res => {
                setChat(old => ({
                    messages: [...res.data.data, ...old.messages],
                    hasMore: res.data.data.length >= messageLimit,
                    pending: false,
                }));
                setLoading(false);
            })
    };

    return {
        chat,
        loading,
        loadMore,
        send,
    }
};

export const useChats = (closed: boolean = false) => {
    const [chats, setChats] = useState<ChatModel[]>([]);

    useEffect(() => {
        Api.chats.list(closed)
            .then(res => setChats(res.data.data));
        // eslint-disable-next-line
    }, []);

    const closeChat = (code: string) => {
        return Api.chats.close(code).then(() => {
            setChats(old => removeItemAtIndex(old, chats.findIndex(c => c.code === code)));
        })
    };

    const createCustom = (code: string, name: string, users: { id: string, name: string }[]) => {
        return Api.chats
            .createCustom(code, name, users.map(u => u.id))
            .then(() => welcomeUsers(code, users.map(u => u.name)))
    };

    const welcomeUsers = (id: string, names: string[]) => {
        return Api.chats.send(id, `Added ${names.join(', ')}`);
    };

    return {
        chats,
        closeChat,
        createCustom,
        welcomeUsers,
    };
}
